import {
  appendRequest,
  chainForVideo,
  connect,
  createVideoNode,
  GENERATOR_NODE_H,
  IMAGE_NODE_W,
  isImage,
  isVideo,
  looseImages,
  newId,
  nodeById,
  NODE_GAP_X,
  NODE_GAP_Y,
  removeNodes,
  videoForImage,
  type Board,
  type BoardImageNode,
  type BoardVideoNode,
} from './canvasBoard';

/**
 * "Um vídeo para cada imagem" / "para cada foto gere um take".
 * Not "fade em cada vídeo" and not a form answer.
 */
const PER_IMAGE =
  /\b(?:para|pra)\s+cada\s+(?:imagem|foto)\b|\bcada\s+(?:imagem|foto)\b[\s\S]{0,80}\b(?:v[ií]deo|take|clipe)\b|\b(?:v[ií]deo|take|clipe)\b[\s\S]{0,48}\b(?:para|pra|por)\s+cada\s+(?:imagem|foto)\b|\bum\s+(?:v[ií]deo|take|clipe)\s+(?:para|pra|por)\s+cada\b/i;

export function isVideoPerImageRequest(text: string): boolean {
  const t = String(text || '').trim();
  if (!t || /^Respostas do formulário:/i.test(t)) return false;
  return PER_IMAGE.test(t);
}

/** "gere o vídeo" / "gerar os videos", without a longer instruction after it. */
export function isGenerateVideoRequest(text: string): boolean {
  const t = String(text || '').trim().replace(/[.!?…]+$/, '');
  return /^ger(?:e|ar)(?:\s+os|\s+o|\s+um)?\s+v[ií]deos?$/i.test(t);
}

/** Motion instruction stored on each generator, without the "para cada imagem" wrapper. */
export function perImageBrief(text: string): string {
  const stripped = String(text || '')
    .replace(/\b(?:para|pra)\s+cada\s+(?:imagem|foto)\b[,:]?\s*/gi, '')
    .replace(/\b(?:um\s+)?(?:v[ií]deo|take|clipe)\s+(?:para|pra|por)\s+cada\s+(?:imagem|foto)\b[,:]?\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return stripped || String(text || '').trim();
}

function ready(n: BoardImageNode): boolean {
  return Boolean(n.phase.imageUrl) && n.phase.status === 'ready';
}

function byPos(a: BoardImageNode, b: BoardImageNode): number {
  return a.x - b.x || a.y - b.y;
}

/** Videos that are not a join/edit/gallery clip. */
function plainVideos(board: Board): BoardVideoNode[] {
  return board.nodes.filter((n): n is BoardVideoNode => isVideo(n) && !n.edit && !n.fromGallery);
}

function ownersOf(board: Board, imageId: string): BoardVideoNode[] {
  return plainVideos(board).filter((v) => chainForVideo(board, v.id).some((n) => n.id === imageId));
}

/** A video whose only input is this image. Selecting the image is enough to know which flow changes. */
function exclusiveVideo(board: Board, imageId: string): BoardVideoNode | undefined {
  return ownersOf(board, imageId).find((v) => {
    const chain = chainForVideo(board, v.id);
    return chain.length === 1 && chain[0].id === imageId;
  });
}

function targetImages(board: Board, selectedIds: string[]): BoardImageNode[] {
  const readyImages = board.nodes.filter(isImage).filter(ready);
  const selected = readyImages.filter((n) => selectedIds.includes(n.id)).sort(byPos);
  if (selected.length) return selected;
  const fromVideos = selectedIds.flatMap((id) => {
    const node = nodeById(board, id);
    if (!isVideo(node) || node.edit || node.fromGallery) return [];
    return chainForVideo(board, id).filter(ready);
  });
  const uniqueVideos = fromVideos.filter((n, i) => fromVideos.findIndex((m) => m.id === n.id) === i).sort(byPos);
  if (uniqueVideos.length) return uniqueVideos;
  const loose = looseImages(board).filter(ready).sort(byPos);
  if (loose.length) return loose;
  return readyImages.sort(byPos);
}

function stamp(video: BoardVideoNode, text: string, brief: string): BoardVideoNode {
  return {
    ...video,
    brief,
    requests: appendRequest(video.requests, text),
    planned: undefined,
    generator: { ...video.generator, prompt: brief, promptSource: 'user' },
  };
}

/**
 * One generator per image.
 * An image that already has a video of its own is a modification of that video:
 * the user does not need to select the video node for the change to be unambiguous.
 * Images that share a flow, or sit loose, each get a new video and leave the shared chain.
 */
function readyChain(board: Board, videoId: string): BoardImageNode[] {
  return chainForVideo(board, videoId).filter((n) => n.phase.status === 'ready' && Boolean(n.phase.imageUrl));
}

/** A video the debit window can quote: a reference take, or a storyboard already planned. */
function canQuote(board: Board, video: BoardVideoNode): boolean {
  if (video.edit || video.fromGallery || video.status === 'generating' || video.status === 'joining') return false;
  if (!readyChain(board, video.id).length) return false;
  if ((video.imageMode || 'keyframes') === 'reference') return true;
  return Boolean(video.planned);
}

/**
 * Videos "gere o vídeo" should confirm.
 * A selected photo quotes its own video. With nothing selected, every take that still has no clip.
 */
export function quoteVideoIds(board: Board, selectedIds: string[]): string[] {
  const selected = selectedIds.map((id) => nodeById(board, id));
  const images = selected.filter(isImage);
  const videos = selected.filter(isVideo);
  if (images.length || videos.length) {
    const found: string[] = [];
    const add = (video: BoardVideoNode | undefined) => {
      if (video && canQuote(board, video) && !found.includes(video.id)) found.push(video.id);
    };
    if (images.length) for (const image of images) add(videoForImage(board, image.id));
    else for (const video of videos) add(video);
    return found;
  }
  return board.nodes.filter(isVideo).filter((v) => canQuote(board, v) && !v.clipUrl).map((v) => v.id);
}

export function applyVideoPerImage(
  board: Board,
  opts: { text: string; selectedIds: string[] },
): { board: Board; created: number; updated: number; videoIds: string[] } | undefined {
  const text = String(opts.text || '').trim();
  if (!isVideoPerImageRequest(text)) return undefined;
  const images = targetImages(board, opts.selectedIds);
  if (!images.length) return { board, created: 0, updated: 0, videoIds: [] };
  const brief = perImageBrief(text);
  const originX = Math.min(...images.map((n) => n.x));
  const originY = Math.min(...images.map((n) => n.y));
  const row = GENERATOR_NODE_H + NODE_GAP_Y;
  /** Decide on the original board. Detaching one photo must not turn the shared video into "this image's own video". */
  const plan = images.map((image) => ({ image, solo: exclusiveVideo(board, image.id) }));
  let next = board;
  let created = 0;
  let updated = 0;
  const videoIds: string[] = [];
  const touchedOwners = new Set<string>();

  plan.forEach(({ image, solo }, index) => {
    const y = originY + index * row;
    if (solo) {
      next = {
        ...next,
        nodes: next.nodes.map((n) => {
          if (n.id === image.id && isImage(n)) return { ...n, x: originX, y };
          if (n.id === solo.id && isVideo(n)) return { ...stamp(n, text, brief), x: originX + IMAGE_NODE_W + NODE_GAP_X, y };
          return n;
        }),
      };
      updated += 1;
      videoIds.push(solo.id);
      return;
    }
    for (const owner of ownersOf(next, image.id)) touchedOwners.add(owner.id);
    next = {
      ...next,
      nodes: next.nodes.map((n) => (n.id === image.id && isImage(n) ? { ...n, x: originX, y } : n)),
      edges: next.edges.filter((e) => e.role === 'ref' || (e.from !== image.id && e.to !== image.id)),
    };
    const video = stamp(
      {
        ...createVideoNode(originX + IMAGE_NODE_W + NODE_GAP_X, y),
        id: newId('video'),
        imageMode: 'reference',
      },
      text,
      brief,
    );
    next = { ...next, nodes: [...next.nodes, video] };
    next = connect(next, image.id, video.id);
    created += 1;
    videoIds.push(video.id);
  });

  const empty = Array.from(touchedOwners).filter((id) => {
    const node = nodeById(next, id);
    if (!isVideo(node) || node.edit || node.fromGallery || node.clipUrl || node.videoUrl) return false;
    return chainForVideo(next, id).length === 0;
  });
  if (empty.length) next = removeNodes(next, empty);
  return { board: next, created, updated, videoIds };
}
